/**
 * Serviço para interação com o microserviço de municípios
 * Fornece métodos para busca e manipulação de dados municipais
 */
import { municipalitiesApi, formatErrorMessage } from './BaseApiService';

function parseBboxString(bbox) {
  const [minx, miny, maxx, maxy] = String(bbox || '')
    .split(',')
    .map((value) => Number.parseFloat(value));

  if ([minx, miny, maxx, maxy].some((value) => Number.isNaN(value))) {
    throw new Error('BBox inválida. Use o formato lng1,lat1,lng2,lat2');
  }

  return { minx, miny, maxx, maxy };
}

function extractMunicipiosFromResponse(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.municipios)) return payload.municipios;

  return [];
}

export async function getMunicipios() {
  try {
    const response = await municipalitiesApi.get('/api/v1/municipios');
    return extractMunicipiosFromResponse(response);
  } catch (error) {
    console.error('[MunicipalitiesService] Erro ao buscar municípios:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao carregar municípios');
  }
}

/**
 * Busca todos os municípios de um estado específico
 *
 * @param {string} uf - Sigla do estado (ex: 'SP', 'RJ')
 * @returns {Promise<Array>} Lista de municípios do estado
 */
export async function getMunicipiosPorEstado(uf) {
  try {
    const municipios = await getMunicipios();
    const ufNormalized = String(uf || '')
      .trim()
      .toUpperCase();
    return municipios.filter(
      (municipio) => String(municipio?.uf || '').toUpperCase() === ufNormalized,
    );
  } catch (error) {
    console.error(`[MunicipalitiesService] Erro ao buscar municípios do estado ${uf}:`, error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || `Falha ao carregar municípios de ${uf}`);
  }
}

/**
 * Busca município pelo código IBGE
 *
 * @param {string} codigoIbge - Código IBGE do município
 * @returns {Promise<Object>} Dados do município
 */
export async function getMunicipioPorCodigoIbge(codigoIbge) {
  try {
    const municipios = await getMunicipios();
    const target = String(codigoIbge || '').trim();
    return (
      municipios.find((municipio) => {
        const codigo =
          municipio?.codigo_ibge || municipio?.cod_ibge || municipio?.ibge || municipio?.codigo;
        return String(codigo || '').trim() === target;
      }) || null
    );
  } catch (error) {
    console.error(
      `[MunicipalitiesService] Erro ao buscar município com código IBGE ${codigoIbge}:`,
      error,
    );
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao buscar município');
  }
}

/**
 * Busca municípios por nome ou parte do nome
 *
 * @param {string} nome - Nome completo ou parcial do município
 * @returns {Promise<Array>} Lista de municípios que correspondem ao nome
 */
export async function buscarMunicipiosPorNome(nome) {
  try {
    const municipios = await getMunicipios();
    if (nome && nome.trim() !== '') {
      const termoBusca = nome.trim().toLowerCase();
      return municipios.filter(
        (municipio) => municipio.nome && municipio.nome.toLowerCase().includes(termoBusca),
      );
    }
    return municipios;
  } catch (error) {
    console.error(`[MunicipalitiesService] Erro ao buscar municípios com nome "${nome}":`, error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao buscar municípios');
  }
}

/**
 * Busca municípios por coordenadas geográficas
 *
 * @param {number} latitude - Latitude do ponto
 * @param {number} longitude - Longitude do ponto
 * @returns {Promise<Object>} Dados do município correspondente às coordenadas
 */
export async function getMunicipioPorCoordenadas(latitude, longitude) {
  try {
    const response = await municipalitiesApi.get('/api/v1/municipios/point', {
      params: {
        latitude,
        longitude,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    console.error(
      `[MunicipalitiesService] Erro ao buscar município por coordenadas [${latitude}, ${longitude}]:`,
      error,
    );
    // eslint-disable-next-line preserve-caught-error
    throw new Error(
      formatErrorMessage(error) || 'Falha ao identificar município pelas coordenadas',
    );
  }
}

/**
 * Busca municípios dentro de um bounding box (caixa delimitadora)
 *
 * @param {string} bbox - String de bounding box no formato 'lng1,lat1,lng2,lat2'
 * @returns {Promise<Array>} Lista de municípios dentro da área especificada
 */
export async function buscarMunicipiosPorBBox(bbox) {
  try {
    const { minx, miny, maxx, maxy } = parseBboxString(bbox);
    const response = await municipalitiesApi.get('/api/v1/municipios/bbox', {
      params: {
        minx,
        miny,
        maxx,
        maxy,
      },
    });
    const municipios = extractMunicipiosFromResponse(response);
    return municipios;
  } catch (error) {
    console.error(`[MunicipalitiesService] Erro ao buscar municípios por bbox [${bbox}]:`, error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao buscar municípios na área');
  }
}

/**
 * Obtém lista de todos os estados brasileiros
 *
 * @returns {Promise<Array>} Lista de estados com sigla e nome
 */
export async function getEstados() {
  try {
    // Como não há endpoint específico para estados, vamos buscar todos os municípios
    // e depois extrair os estados únicos
    const municipios = await getMunicipios();

    // Extrair estados únicos
    const estadosMap = {};
    municipios.forEach((municipio) => {
      if (municipio.uf) {
        estadosMap[municipio.uf] = true;
      }
    });

    // Converter para formato esperado pelo frontend
    return Object.keys(estadosMap).map((uf) => ({
      sigla: uf,
      nome: getEstadoNome(uf),
    }));
  } catch (error) {
    console.error('[MunicipalitiesService] Erro ao buscar lista de estados:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao carregar estados');
  }
}

// Helper para obter nome do estado pela sigla
function getEstadoNome(uf) {
  const estados = {
    AC: 'Acre',
    AL: 'Alagoas',
    AM: 'Amazonas',
    AP: 'Amapá',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MG: 'Minas Gerais',
    MS: 'Mato Grosso do Sul',
    MT: 'Mato Grosso',
    PA: 'Pará',
    PB: 'Paraíba',
    PE: 'Pernambuco',
    PI: 'Piauí',
    PR: 'Paraná',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RO: 'Rondônia',
    RR: 'Roraima',
    RS: 'Rio Grande do Sul',
    SC: 'Santa Catarina',
    SE: 'Sergipe',
    SP: 'São Paulo',
    TO: 'Tocantins',
  };

  return estados[uf] || uf;
}

export default {
  getMunicipios,
  getMunicipiosPorEstado,
  getMunicipioPorCodigoIbge,
  buscarMunicipiosPorNome,
  getMunicipioPorCoordenadas,
  getEstados,
  buscarMunicipiosPorBBox,
};
